import { useUnmountableCallback } from 'components/shared/hooks';
import { useEffect, useRef, useState } from 'react';
import { GridApi } from 'ag-grid-community';

/**
 * Size columns exactly once per grid lifecycle: fit to viewport only (no content-based sizing).
 * This prevents re-scale on click/filter/selection; we keep one consistent "fit to window" layout.
 */
export const useGridComposer = (gridApi: GridApi | null, disableAutoSize: boolean) => {
  const [gridVisible, setGridVisible] = useState(false);
  const mounted = useRef(false);
  const hasSizedOnce = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      hasSizedOnce.current = false;
    };
  }, []);

  const composeGrid = useUnmountableCallback(() => {
    if (disableAutoSize && mounted.current) {
      setGridVisible(true);
      return;
    }
    if (!gridApi) return;
    if (hasSizedOnce.current) {
      setGridVisible(true);
      return;
    }
    setGridVisible(false);
    // Only fit to viewport – never autoSizeAllColumns, so nothing can "re-apply" content width on click
    gridApi.sizeColumnsToFit({ defaultMinWidth: 50 });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mounted.current) return;
        hasSizedOnce.current = true;
        setGridVisible(true);
      });
    });
  }, [disableAutoSize, gridApi]);

  return { composeGrid, gridVisible };
};
